import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listMeituanVerifyLogs,
  type MeituanVerifyLog as MeituanVerifyLogRecord,
} from '../services/meituanVerifyLogService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const processStatusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'orange' },
  1: { text: '已处理', color: 'green' },
  2: { text: '处理失败', color: 'red' },
};

const verifyStatusMap: Record<string, { text: string; color: string }> = {
  '1': { text: '核销成功', color: 'green' },
  '2': { text: '核销失败', color: 'red' },
};

const verifyChannelMap: Record<string, string> = {
  '1': '第三方平台核销',
  '3': '美团券码核销',
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const formatJSON = (value?: string) => {
  if (!value) return '-';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const MeituanVerifyLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<MeituanVerifyLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meituanOrderId, setMeituanOrderId] = useState('');
  const [serviceOrderNo, setServiceOrderNo] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [verifyStatus, setVerifyStatus] = useState<string>();
  const [processStatus, setProcessStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<MeituanVerifyLogRecord | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanVerifyLogs({
        page: nextPage,
        pageSize: nextPageSize,
        meituanOrderId: meituanOrderId || undefined,
        serviceOrderNo: serviceOrderNo || undefined,
        serialNumber: serialNumber || undefined,
        verifyStatus,
        processStatus,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载验券日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadData(1, pageSize);
  };

  const handleReset = () => {
    setMeituanOrderId('');
    setServiceOrderNo('');
    setSerialNumber('');
    setVerifyStatus(undefined);
    setProcessStatus(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => void loadData(1, pageSize), 0);
  };

  const columns: ColumnsType<MeituanVerifyLogRecord> = [
    {
      title: '美团订单ID',
      dataIndex: 'meituanOrderId',
      key: 'meituanOrderId',
      width: 180,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '平台订单号',
      dataIndex: 'serviceOrderNo',
      key: 'serviceOrderNo',
      width: 170,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '券码',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 220,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '核销状态',
      dataIndex: 'verifyStatus',
      key: 'verifyStatus',
      width: 110,
      render: value => {
        const item = verifyStatusMap[value] || { text: value || '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '处理状态',
      dataIndex: 'processStatus',
      key: 'processStatus',
      width: 110,
      render: value => {
        const item = processStatusMap[value] || { text: value ?? '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '核销渠道',
      dataIndex: 'verifyChannel',
      key: 'verifyChannel',
      width: 140,
      render: value => verifyChannelMap[value] || value || '-',
    },
    {
      title: '处理说明',
      dataIndex: 'processMessage',
      key: 'processMessage',
      width: 280,
      ellipsis: true,
      render: value => value ? (
        <Tooltip title={value}>
          <Text>{value}</Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: '推送时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: value => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setCurrentLog(record);
            setDetailVisible(true);
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>验券日志</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="美团订单ID" value={meituanOrderId} onChange={event => setMeituanOrderId(event.target.value)} allowClear style={{ width: 180 }} />
          <Input placeholder="平台订单号" value={serviceOrderNo} onChange={event => setServiceOrderNo(event.target.value)} allowClear style={{ width: 180 }} />
          <Input placeholder="券码" value={serialNumber} onChange={event => setSerialNumber(event.target.value)} allowClear style={{ width: 220 }} />
          <Select placeholder="核销状态" value={verifyStatus} onChange={setVerifyStatus} allowClear style={{ width: 120 }}>
            <Select.Option value="1">核销成功</Select.Option>
            <Select.Option value="2">核销失败</Select.Option>
          </Select>
          <Select placeholder="处理状态" value={processStatus} onChange={setProcessStatus} allowClear style={{ width: 120 }}>
            <Select.Option value="0">待处理</Select.Option>
            <Select.Option value="1">已处理</Select.Option>
            <Select.Option value="2">处理失败</Select.Option>
          </Select>
          <RangePicker value={dateRange} onChange={setDateRange} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </Space>
      </Card>
      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={logs}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: value => `共 ${value} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || 10);
            },
          }}
        />
      </Card>
      <Modal title="验券日志详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={900}>
        {currentLog && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="回调ID">{currentLog.callbackId || '-'}</Descriptions.Item>
              <Descriptions.Item label="消息类型">{currentLog.messageType || '-'}</Descriptions.Item>
              <Descriptions.Item label="美团订单ID">{currentLog.meituanOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="平台订单ID">{currentLog.serviceOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="平台订单号">{currentLog.serviceOrderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="券码">{currentLog.serialNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="门店ID">{currentLog.shopId || '-'}</Descriptions.Item>
              <Descriptions.Item label="业务编码">{currentLog.opBizCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="核销状态">{verifyStatusMap[currentLog.verifyStatus]?.text || currentLog.verifyStatus || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理状态">{processStatusMap[currentLog.processStatus]?.text || currentLog.processStatus}</Descriptions.Item>
              <Descriptions.Item label="核销渠道">{verifyChannelMap[currentLog.verifyChannel] || currentLog.verifyChannel || '-'}</Descriptions.Item>
              <Descriptions.Item label="交易类型">{currentLog.type || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理说明" span={2}>{currentLog.processMessage || '-'}</Descriptions.Item>
              <Descriptions.Item label="推送时间">{formatDateTime(currentLog.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(currentLog.updatedAt)}</Descriptions.Item>
            </Descriptions>
            <Card title="原始回调数据" size="small">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatJSON(currentLog.rawPayload)}</pre>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MeituanVerifyLog;
